import { createContext, useContext, ReactNode, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import { UpgradeModal, type PlanEnforcementError } from "@/components/UpgradeModal";

export type PlanTier = "lite" | "core" | "pro" | "unlimited";

export interface PlanFeatures {
  maxUsers: number;
  maxQuotesPerMonth: number;
  maxJobsPerMonth: number;
  aiAutomations: boolean;
  unlimitedSms: boolean;
  fileStorageLimitMb: number;
  clientPortal: boolean;
  advancedScheduling: boolean;
  apiAccess: boolean;
}

export interface TenantUsage {
  jobsThisMonth: number;
  quotesThisMonth: number;
  activeUsers: number;
  storageUsedMb: number;
  smsThisMonth: number;
}

export interface PlanInfo {
  plan: PlanTier;
  features: PlanFeatures;
  usage: TenantUsage;
  subscriptionStatus?: string | null;
}

interface PlanContextType {
  plan: PlanTier;
  features: PlanFeatures;
  usage: TenantUsage;
  isLoading: boolean;
  canCreateUser: boolean;
  canCreateJob: boolean;
  canCreateQuote: boolean;
  subscriptionStatus: string | null;
  isSubscriptionActive: boolean;
  showUpgradeModal: (error: PlanEnforcementError) => void;
  checkFeature: (feature: keyof PlanFeatures) => boolean;
}

const PlanContext = createContext<PlanContextType | undefined>(undefined);

export function PlanProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [upgradeError, setUpgradeError] = useState<PlanEnforcementError | null>(null);
  const [showModal, setShowModal] = useState(false);

  const { data, isLoading } = useQuery<PlanInfo>({
    queryKey: ["/plan"],
    enabled: isAuthenticated,
    retry: false,
  });

  const showUpgradeModal = useCallback((error: PlanEnforcementError) => {
    setUpgradeError(error);
    setShowModal(true);
  }, []);

  const checkFeature = useCallback((feature: keyof PlanFeatures): boolean => {
    if (!data?.features) return false;
    const value = data.features[feature];
    return typeof value === 'boolean' ? value : value > 0;
  }, [data]);

  const subscriptionStatus = data?.subscriptionStatus ?? null;
  // Subscription is active if status is active, trialing, or null/undefined (no status yet = active)
  const isSubscriptionActive = !subscriptionStatus || subscriptionStatus === "active" || subscriptionStatus === "trialing";

  const quotaAllowsMore = (used: number, cap: number | null | undefined) => {
    if (cap === null || cap === undefined) return true;
    if (cap === Infinity || cap === -1) return true;
    return used < cap;
  };

  const value: PlanContextType = {
    plan: data?.plan || "unlimited",
    subscriptionStatus,
    isSubscriptionActive,
    features: data?.features || {
      maxUsers: 999,
      maxQuotesPerMonth: 999,
      maxJobsPerMonth: 999,
      aiAutomations: true,
      unlimitedSms: true,
      fileStorageLimitMb: 50000,
      clientPortal: true,
      advancedScheduling: true,
      apiAccess: true,
    },
    usage: data?.usage || {
      jobsThisMonth: 0,
      quotesThisMonth: 0,
      activeUsers: 0,
      storageUsedMb: 0,
      smsThisMonth: 0,
    },
    isLoading,
    canCreateUser:
      data?.features && data?.usage
        ? quotaAllowsMore(data.usage.activeUsers, data.features.maxUsers as number | null | undefined)
        : false,
    canCreateJob:
      data?.features && data?.usage
        ? quotaAllowsMore(data.usage.jobsThisMonth, data.features.maxJobsPerMonth as number | null | undefined)
        : false,
    canCreateQuote:
      data?.features && data?.usage
        ? quotaAllowsMore(data.usage.quotesThisMonth, data.features.maxQuotesPerMonth as number | null | undefined)
        : false,
    showUpgradeModal,
    checkFeature,
  };

  return (
    <PlanContext.Provider value={value}>
      {children}
      <UpgradeModal
        open={showModal}
        onOpenChange={setShowModal}
        error={upgradeError}
      />
    </PlanContext.Provider>
  );
}

export function usePlan() {
  const context = useContext(PlanContext);
  if (context === undefined) {
    throw new Error("usePlan must be used within a PlanProvider");
  }
  return context;
}
